import { ChangelogViewer } from '@/components/ChangelogViewer';
import { useCurrentUser } from '@/features/users/users.hooks';
import {
  hasServiceWorkerChoice,
  installServiceWorker,
} from '@/services/swRegistration';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import {
  getSyncStatus,
  isCacheInvalidationMessage,
  isMinimumClientVersionMessage,
  isUpdateReadyMessage,
  onSwMessage,
  requestSync,
  type VersionInfo,
} from './swBridge';
import { handleMinimumClientVersion, promptUpdateReady } from './updateFlow';
import { syncDelayMs, waitForNetworkIdle } from './syncScheduler';
import { useServiceWorkerEnabled } from './useSyncStatus';

/** Incremental sync interval — matches the current release. */
const SYNC_INTERVAL_MS = 15 * 60_000;

/**
 * Watchdog cadence. The browser can kill the SW mid-sync (~5 min lifetime
 * cap); the killed run leaves an in-flight marker behind and the status
 * reads 'aborted'. Polling re-triggers until a run completes — sync is
 * resumable, so each re-trigger continues where the dead run stopped. The
 * poll interval doubles as the re-trigger debounce.
 */
const WATCHDOG_INTERVAL_MS = 60_000;

/** Quiet period of the query cache before a load-time sync may start. */
const NETWORK_IDLE_MS = 2_000;

/**
 * SW resource keys → TanStack Query key prefixes. Resources whose feature
 * uses a different query-key prefix than the API resource name map here;
 * everything else invalidates its own name.
 */
const RESOURCE_QUERY_PREFIXES: Record<string, string[]> = {
  lists: ['checklists', 'templates'],
  catalog_content: ['docs.catalog'],
};

/**
 * Headless orchestrator for the offline service worker (mounted once in the
 * router root layout, see app/addRouter.tsx):
 * - triggers sync once the page is network-idle and the last run is older
 *   than the interval, on reconnect, and every 15 minutes; the SW joins
 *   concurrent triggers into a single run (single-flight)
 * - re-fetches affected queries when the SW broadcasts CACHE_INVALIDATION
 *   (sync fetched newer data / another window's mutation changed the cache)
 * - hosts the release-notes dialog for the update toast's "What's new"
 *   button (the waiting SW's changelog, not this build's)
 */
export function OfflineManager(): React.ReactNode {
  const queryClient = useQueryClient();
  const swEnabled = useServiceWorkerEnabled();
  const user = useCurrentUser();
  const [waitingUpdate, setWaitingUpdate] = React.useState<VersionInfo>();
  const [changelogOpen, setChangelogOpen] = React.useState(false);

  // Remote install: the per-user cross-client default
  // (settings.offline_sync_default_enabled) installs the SW on devices where
  // the user never made an explicit choice. An explicit install/uninstall
  // (hasServiceWorkerChoice) always wins — a manual uninstall is never
  // overridden by the default.
  const defaultEnabled =
    (user?.settings as { offline_sync_default_enabled?: boolean } | undefined)
      ?.offline_sync_default_enabled === true;
  React.useEffect(() => {
    if (swEnabled || !defaultEnabled || hasServiceWorkerChoice()) {
      return;
    }
    void installServiceWorker()
      .then(() => requestSync())
      .catch(() => {
        // unsupported browser / registration failure — regular opt-in via
        // the sync-status panel remains available
      });
  }, [swEnabled, defaultEnabled]);

  React.useEffect(() => {
    if (!swEnabled) {
      return; // SW is opt-in — nothing to orchestrate until installed
    }
    const unsubscribe = onSwMessage((data) => {
      if (isCacheInvalidationMessage(data)) {
        for (const resource of data.payload.resourceKeys) {
          const prefixes = RESOURCE_QUERY_PREFIXES[resource] ?? [resource];
          for (const prefix of prefixes) {
            void queryClient.invalidateQueries({ queryKey: [prefix] });
          }
        }
      }
      if (isUpdateReadyMessage(data)) {
        void promptUpdateReady((info) => {
          setWaitingUpdate(info);
          setChangelogOpen(true);
        });
      }
      if (isMinimumClientVersionMessage(data)) {
        void handleMinimumClientVersion(data.payload.version);
      }
    });

    // Load-time sync waits for the page's own data to land and skips when
    // the last run (this or another window) is more recent than the
    // interval. The loop re-evaluates after each trigger, so the next run
    // is spaced from that sync's completion rather than from page load.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let idle: ReturnType<typeof waitForNetworkIdle> | undefined;
    const tick = async () => {
      idle = waitForNetworkIdle(queryClient, NETWORK_IDLE_MS);
      await idle.promise;
      const status = await getSyncStatus();
      if (cancelled) return;
      const wait = syncDelayMs(
        status?.lastSyncedAt,
        Date.now(),
        SYNC_INTERVAL_MS,
      );
      if (wait > 0) {
        timer = setTimeout(tick, wait);
        return;
      }
      requestSync();
      timer = setTimeout(tick, SYNC_INTERVAL_MS);
    };
    void tick();
    window.addEventListener('online', requestSync);

    const watchdog = setInterval(() => {
      void getSyncStatus().then((status) => {
        if (status?.state === 'aborted') {
          requestSync();
        }
        // Ping-detected offline (server down, LAN up) never fires a browser
        // 'online' event — re-ping until the server is back.
        if (status?.state === 'offline' && navigator.onLine) {
          requestSync();
        }
      });
    }, WATCHDOG_INTERVAL_MS);

    return () => {
      unsubscribe();
      cancelled = true;
      idle?.cancel();
      clearTimeout(timer);
      clearInterval(watchdog);
      window.removeEventListener('online', requestSync);
    };
  }, [queryClient, swEnabled]);

  if (!waitingUpdate?.changelog) {
    return null;
  }
  return (
    <ChangelogViewer
      value={waitingUpdate.changelog}
      open={changelogOpen}
      onOpenChange={setChangelogOpen}
    />
  );
}
